import { useEffect, useRef, useState } from 'react';
import ErrorUtils from 'src/utils/ErrorUtils';
import {
    CliRunSourcesEntry,
    CliSourcesFetchers,
    CliSourcesResult,
    resultFromRunSources,
    SOURCES_BATCH_FAILED,
} from 'src/components/reports/lefReport/cliSources';

export type CliSourcesState =
    | { status: 'loading' }
    | { status: 'error'; message: string }
    | CliSourcesResult;

/** CLIs per batch call; the backend accepts at most 50. */
export const CLI_SOURCES_BATCH_SIZE = 50;

/** Batch calls in flight at once. Each one fans out on the server, so keep this low. */
export const CLI_SOURCES_BATCH_CONCURRENCY = 2;

/**
 * Session-wide cache of finished lookups, keyed by period + CLI. A CLI's sources for a closed
 * period do not change within a session, so re-viewing a report, paging, switching Functional
 * Segment back to "All" or re-expanding a segment never refetches. Errors are not cached, so a
 * transient failure retries on the next reveal.
 */
const resultCache = new Map<string, CliSourcesResult>();
const cacheKey = (period: string, cli: string) => `${period}|${cli}`;

/** Test hook: clears the session cache. */
export function clearCliSourcesCache(): void {
    resultCache.clear();
}

/**
 * Loads source chips (Story 3.3) for the CLIs currently revealed in the LEF grid, lazily, through
 * POST /cli-run-sources in batches of up to CLI_SOURCES_BATCH_SIZE, at most CLI_SOURCES_BATCH_CONCURRENCY
 * at a time. A batch call that fails as a whole marks its CLIs as errored, so they retry on the next
 * reveal. Changing the report context drops in-flight results; finished ones are served from the
 * session cache.
 */
export function useCliSources(options: {
    enabled: boolean;
    contextKey: string | null;
    /** Calculation-execution period ("MAY-2026"); null disables loading. */
    executionPeriod: string | null;
    clis: ReadonlyArray<string>;
    fetchers: CliSourcesFetchers;
}): { [cli: string]: CliSourcesState } {
    const { enabled, contextKey, executionPeriod, clis, fetchers } = options;
    const [states, setStates] = useState<{ [cli: string]: CliSourcesState }>({});
    const requested = useRef(new Set<string>());
    const queue = useRef<string[]>([]);
    const running = useRef(0);
    const generation = useRef(0);
    const latest = useRef({ executionPeriod, fetchers });
    latest.current = { executionPeriod, fetchers };

    useEffect(() => {
        setStates({});
        requested.current = new Set();
        queue.current = [];
        running.current = 0;
        return () => {
            generation.current += 1;
        };
    }, [contextKey, executionPeriod]);

    useEffect(() => {
        if (!enabled || !executionPeriod) {
            return;
        }
        const fresh = clis.filter((cli) => !requested.current.has(cli));
        if (fresh.length === 0) {
            return;
        }
        fresh.forEach((cli) => requested.current.add(cli));

        const cached: { [cli: string]: CliSourcesState } = {};
        const toFetch: string[] = [];
        fresh.forEach((cli) => {
            const hit = resultCache.get(cacheKey(executionPeriod, cli));
            if (hit) {
                cached[cli] = hit;
            } else {
                toFetch.push(cli);
            }
        });
        queue.current.push(...toFetch);
        setStates((previous) => {
            const next = { ...previous, ...cached };
            toFetch.forEach((cli) => (next[cli] = { status: 'loading' }));
            return next;
        });

        const requestGeneration = generation.current;
        const isStale = () => requestGeneration !== generation.current;

        /** Caches final results, re-arms errored CLIs for the next reveal, and publishes the states. */
        const settle = (period: string, results: { [cli: string]: CliSourcesState }) => {
            Object.entries(results).forEach(([cli, state]) => {
                if (state.status === 'loaded' || state.status === 'unavailable') {
                    resultCache.set(cacheKey(period, cli), state);
                } else {
                    requested.current.delete(cli);
                }
            });
            setStates((previous) => ({ ...previous, ...results }));
        };

        const runBatch = (batch: string[], period: string, fetchBatch: CliSourcesFetchers['fetchRunSourcesBatch']) => {
            running.current += 1;
            // Through a promise so a synchronous throw is handled like a rejected call.
            Promise.resolve()
                .then(() => fetchBatch(batch, period))
                .then(
                    (entries) => {
                        const byCli = new Map<string, CliRunSourcesEntry>();
                        (entries ?? []).forEach((entry) => byCli.set(entry.calculationNumber, entry));
                        const results: { [cli: string]: CliSourcesState } = {};
                        batch.forEach((cli) => (results[cli] = resultFromRunSources(byCli.get(cli))));
                        return results;
                    },
                    (error) => {
                        // The whole call failed: every CLI in it is errored, and retries on its next reveal.
                        const message = ErrorUtils.getMessage(error) || SOURCES_BATCH_FAILED;
                        const results: { [cli: string]: CliSourcesState } = {};
                        batch.forEach((cli) => (results[cli] = { status: 'error', message }));
                        return results;
                    }
                )
                .then((results) => {
                    if (isStale()) {
                        return;
                    }
                    running.current -= 1;
                    settle(period, results);
                    pump();
                });
        };

        const pump = () => {
            const { executionPeriod: period, fetchers: current } = latest.current;
            while (running.current < CLI_SOURCES_BATCH_CONCURRENCY && queue.current.length > 0) {
                runBatch(queue.current.splice(0, CLI_SOURCES_BATCH_SIZE), period as string, current.fetchRunSourcesBatch);
            }
        };
        pump();
    // contextKey is a dependency so a context change (which clears `requested` above, in the same
    // commit) re-requests every CLI that is still revealed.
    }, [enabled, contextKey, executionPeriod, clis.join('|')]);

    return states;
}
